import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Terminal } from "lucide-react";

function parseConsole(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

const TYPE_TONE = {
    error: "text-red-400",
    warn: "text-amber-300",
    log: "text-gray-100",
};

function formatLine(log) {
    const ts = log.ts ? new Date(log.ts).toISOString().slice(11, 19) : "--:--:--";
    const tag = (log.type || "log").toUpperCase().padEnd(5, " ");
    return { ts, tag, message: log.message || "", tone: TYPE_TONE[log.type] || TYPE_TONE.log };
}

function ActivityConsoleOutput({ raw, loading }) {
    const { t } = useTranslation();
    const logs = useMemo(() => parseConsole(raw), [raw]);

    if (loading && logs.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="flex items-center gap-2 px-6 py-3 bg-gray-50 border-b border-gray-200">
                    <Terminal className="w-4 h-4 text-blue-600" />
                    <p className="text-sm font-semibold text-gray-900">
                        {t("activity.consoleOutput") || "Console output"}
                    </p>
                </div>
                <div className="bg-gray-900 p-4 space-y-2">
                    {[0, 1, 2, 3].map((i) => (
                        <div
                            key={i}
                            className="h-3 bg-gray-700 rounded animate-pulse"
                            style={{ width: `${50 + (i * 11) % 40}%` }}
                        />
                    ))}
                </div>
            </div>
        );
    }

    if (logs.length === 0) return null;

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="flex items-center gap-2 px-6 py-3 bg-gray-50 border-b border-gray-200">
                <Terminal className="w-4 h-4 text-blue-600" />
                <p className="text-sm font-semibold text-gray-900">
                    {t("activity.consoleOutput") || "Console output"}
                </p>
                <span className="ml-auto text-xs text-gray-400 font-mono tabular-nums">
                    {logs.length} {t("activity.lines") || "lines"}
                </span>
            </div>
            <pre className="bg-gray-900 text-xs font-mono p-4 overflow-x-auto max-h-96 leading-relaxed">
                {logs.map((log, i) => {
                    const line = formatLine(log);
                    return (
                        <div key={i} className="whitespace-pre-wrap break-all">
                            <span className="text-gray-500 mr-2">{line.ts}</span>
                            <span className={`${line.tone} mr-2 font-semibold`}>
                                {line.tag}
                            </span>
                            <span className={line.tone}>{line.message}</span>
                        </div>
                    );
                })}
            </pre>
        </div>
    );
}

export default ActivityConsoleOutput;
